import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  FormControl,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  CreditCard as CreditCardIcon,
  VpnKey as VpnKeyIcon,
  Receipt as ReceiptIcon,
  Person as PersonIcon,
  ExitToApp as ExitToAppIcon,
  Help as HelpIcon,
  Timeline as TimelineIcon,
  Terminal as TerminalIcon,
  Palette as PaletteIcon,
  Collections as GalleryIcon,
  Cloud as CloudIcon,
} from '@mui/icons-material';
import useAuth from '../../features/auth/hooks/useAuth';
import { logoutUser } from '../../features/auth/api/authApi';
import { logoColors, ThemeName, themeNames } from '../../config/themes';
import { useThemeContext } from '../../hooks/useTheme';
import { brand } from '../../config/brand';

// Clean Loxia Logo component
const LoxiaLogo: React.FC<{ size?: 'small' | 'medium' | 'large' }> = ({ size = 'medium' }) => {
  const sizeMap = {
    small: 44,    // Mobile size
    medium: 56,   // Desktop size
    large: 68,    // Large size
  };

  const logoSize = sizeMap[size];

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        transition: 'transform 0.2s ease',
        '&:hover': {
          transform: 'scale(1.05)',
        },
      }}
    >
      {/* Real Logo Image */}
      <Box
        component="img"
        src={brand.logoPath}
        alt={`${brand.name} Logo`}
        sx={{
          width: logoSize,
          height: logoSize,
          objectFit: 'contain',
          p: 0.5,
        }}
      />

      {/* Brand text — use graphic if provided, otherwise render text */}
      {brand.logoTextPath ? (
        <Box
          component="img"
          src={brand.logoTextPath}
          alt={brand.productName}
          sx={{
            height: size === 'large' ? 40 : size === 'medium' ? 32 : 26,
            objectFit: 'contain',
          }}
        />
      ) : (
        <Box>
          <Typography
            variant="h6"
            component="span"
            sx={{
              fontWeight: 800,
              fontSize: size === 'large' ? '1.8rem' : size === 'medium' ? '1.5rem' : '1.2rem',
              color: 'text.primary',
              letterSpacing: '-0.02em',
            }}
          >
            {brand.productName.toUpperCase()}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              fontWeight: 500,
              fontSize: size === 'large' ? '0.8rem' : '0.7rem',
              color: 'text.secondary',
              letterSpacing: '0.5px',
              lineHeight: 1,
              mt: -0.5,
            }}
          >
            BY LOXIA
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// Theme Selector Component
const ThemeSelector: React.FC = () => {
  const { currentTheme, changeTheme } = useThemeContext();

  const handleThemeChange = (event: SelectChangeEvent<string>) => {
    changeTheme(event.target.value as ThemeName);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <PaletteIcon sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
      <FormControl size="small" sx={{ minWidth: 100 }}>
        <Select
          value={currentTheme}
          onChange={handleThemeChange}
          sx={{
            '& .MuiSelect-select': {
              py: 0.5,
              fontSize: '0.875rem',
              fontWeight: 500,
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
          }}
        >
          {Object.entries(themeNames).map(([key, name]) => (
            <MenuItem key={key} value={key}>
              {name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

const Header: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isAuthenticated, user } = useAuth();
  
  // User menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  
  // Mobile drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Handle user menu open
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  // Handle user menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Handle logout
  const handleLogout = () => {
    logoutUser();
    navigate('/login');
    handleMenuClose();
  };

  // Toggle drawer
  const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' ||
        (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  // Navigation items for authenticated users - INCLUDING PAYMENT HISTORY
  const navItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'API Keys', icon: <VpnKeyIcon />, path: '/api-keys' },
    { text: 'Usage Analytics', icon: <TimelineIcon />, path: '/usage' },
    { text: 'Remote Sessions', icon: <CloudIcon />, path: '/sessions' },
    { text: 'Gallery', icon: <GalleryIcon />, path: '/gallery' },
    { text: 'Payment', icon: <CreditCardIcon />, path: '/payment' },
    { text: 'Payment History', icon: <ReceiptIcon />, path: '/payments-history' },
    { text: 'Documentation', icon: <HelpIcon />, url: brand.links.npm, external: true },
  ];

  // Mobile drawer content
  const drawerContent = (
    <Box
      sx={{ 
        width: 280,
        height: '100%',
        backgroundColor: 'background.paper',
      }}
      role="presentation"
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
    >
      <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
          <Avatar 
            sx={{ 
              mb: 2, 
              width: 64, 
              height: 64,
              backgroundColor: 'primary.main',
              fontSize: '1.5rem',
              fontWeight: 700,
            }}
          >
            {user?.firstName?.charAt(0) || user?.email?.charAt(0) || 'U'}
          </Avatar>
          <Typography variant="h6" fontWeight={600} color="text.primary">
            {user?.firstName ? `${user.firstName} ${user.lastName}` : user?.email}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user?.accountType || 'Free Plan'}
          </Typography>
        </Box>
        <ThemeSelector />
      </Box>
      
      <List sx={{ p: 1 }}>
        {navItems.map((item) => (
          <ListItem 
            button 
            key={item.text} 
            component={item.external ? "a" : RouterLink}
            href={item.external ? item.url : undefined}
            to={item.external ? undefined : item.path}
            target={item.external ? "_blank" : undefined}
            rel={item.external ? "noopener noreferrer" : undefined}
            sx={{
              borderRadius: 1,
              mb: 0.5,
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <ListItemIcon sx={{ color: 'text.secondary', minWidth: 40 }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText 
              primary={item.text}
              primaryTypographyProps={{ 
                fontWeight: 500,
                fontSize: '0.95rem',
              }}
            />
          </ListItem>
        ))}
        
        <Divider sx={{ my: 1 }} />
        
        <ListItem 
          button 
          onClick={handleLogout}
          sx={{
            borderRadius: 1,
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: 'error.main',
              color: 'error.contrastText',
              '& .MuiListItemIcon-root': {
                color: 'error.contrastText',
              },
            },
          }}
        >
          <ListItemIcon sx={{ color: 'error.main', minWidth: 40 }}>
            <ExitToAppIcon />
          </ListItemIcon>
          <ListItemText 
            primary="Logout"
            primaryTypographyProps={{ 
              fontWeight: 500,
              fontSize: '0.95rem',
            }}
          />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        backgroundColor: theme.palette.mode === 'light'
          ? 'rgba(255, 255, 255, 0.7)'
          : 'rgba(18, 18, 18, 0.7)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderBottom: '1px solid',
        borderColor: theme.palette.mode === 'light'
          ? 'rgba(0, 0, 0, 0.08)'
          : 'rgba(255, 255, 255, 0.08)',
      }}
    >
      <Toolbar sx={{ px: { xs: 2, sm: 3 } }}>
        <Box
          component={RouterLink}
          to="/"
          sx={{
            textDecoration: 'none',
            color: 'inherit',
            flexGrow: 1,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <LoxiaLogo size={isMobile ? 'small' : 'medium'} />
        </Box>

        {/* Theme Selector (desktop only) */}
        {!isMobile && (
          <Box sx={{ mr: 2 }}>
            <ThemeSelector />
          </Box>
        )}

        {isAuthenticated ? (
          // Authenticated user navigation
          <>
            {isMobile ? (
              // Mobile navigation
              <>
                <IconButton
                  edge="end"
                  color="inherit"
                  aria-label="open navigation menu"
                  onClick={toggleDrawer(true)}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    backgroundColor: 'background.paper',
                    color: 'text.primary',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <MenuIcon />
                </IconButton>
                
                <Drawer
                  anchor="right"
                  open={drawerOpen}
                  onClose={toggleDrawer(false)}
                >
                  {drawerContent}
                </Drawer>
              </>
            ) : (
              // Desktop navigation
              <>
                <Box sx={{ display: 'flex', gap: 1, mr: 2 }}>
                  {navItems.slice(0, 4).map((item) => (
                    <Button
                      key={item.text}
                      color="inherit"
                      component={item.external ? "a" : RouterLink}
                      href={item.external ? item.url : undefined}
                      to={item.external ? undefined : item.path}
                      target={item.external ? "_blank" : undefined}
                      rel={item.external ? "noopener noreferrer" : undefined}
                      startIcon={item.icon}
                      sx={{
                        px: 2,
                        py: 1,
                        fontWeight: 500,
                        color: 'text.secondary',
                        border: 1,
                        borderColor: 'transparent',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          color: 'text.primary',
                          borderColor: 'divider',
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      {item.text}
                    </Button>
                  ))}
                </Box>
                
                <IconButton
                  onClick={handleMenuOpen}
                  color="inherit"
                  aria-controls={menuOpen ? 'user-account-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={menuOpen ? 'true' : undefined}
                  sx={{
                    border: 1,
                    borderColor: 'primary.main',
                    backgroundColor: 'background.paper',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <Avatar 
                    sx={{ 
                      width: 32, 
                      height: 32,
                      backgroundColor: 'primary.main',
                      fontSize: '0.9rem',
                      fontWeight: 700,
                    }}
                  >
                    {user?.firstName?.charAt(0) || user?.email?.charAt(0) || 'U'}
                  </Avatar>
                </IconButton>
                
                <Menu
                  id="user-account-menu"
                  anchorEl={anchorEl}
                  open={menuOpen}
                  onClose={handleMenuClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  PaperProps={{
                    sx: {
                      mt: 1,
                      border: 1,
                      borderColor: 'divider',
                      minWidth: 200,
                    },
                  }}
                >
                  <MenuItem 
                    onClick={() => { navigate('/profile'); handleMenuClose(); }}
                    sx={{ py: 1.5 }}
                  >
                    <PersonIcon sx={{ mr: 2, color: 'text.secondary' }} />
                    Profile Settings
                  </MenuItem>
                  
                  <MenuItem 
                    onClick={() => { navigate('/payments-history'); handleMenuClose(); }}
                    sx={{ py: 1.5 }}
                  >
                    <ReceiptIcon sx={{ mr: 2, color: 'text.secondary' }} />
                    Payment History
                  </MenuItem>
                  
                  <MenuItem 
                    component="a"
                    href={brand.links.npm}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleMenuClose}
                    sx={{ py: 1.5 }}
                  >
                    <HelpIcon sx={{ mr: 2, color: 'text.secondary' }} />
                    Documentation
                  </MenuItem>
                  
                  <Divider />
                  
                  <MenuItem 
                    onClick={handleLogout}
                    sx={{ 
                      py: 1.5,
                      color: 'error.main',
                      '&:hover': {
                        backgroundColor: 'error.main',
                        color: 'error.contrastText',
                        '& .MuiSvgIcon-root': {
                          color: 'error.contrastText',
                        },
                      },
                    }}
                  >
                    <ExitToAppIcon sx={{ mr: 2 }} />
                    Logout
                  </MenuItem>
                </Menu>
              </>
            )}
          </>
        ) : (
          // Non-authenticated navigation
          <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, alignItems: 'center' }}>
            <Button
              color="inherit"
              component={RouterLink}
              to="/gallery"
              sx={{
                display: { xs: 'none', sm: 'inline-flex' },
                px: 2,
                py: 1,
                fontWeight: 600,
                color: 'text.secondary',
                '&:hover': {
                  color: 'text.primary',
                  backgroundColor: 'action.hover',
                },
              }}
            >
              Gallery
            </Button>
            <Button
              color="inherit"
              component={RouterLink}
              to="/login"
              sx={{
                px: { xs: 2, sm: 3 },
                py: 1,
                minHeight: 40,
                border: 1,
                borderColor: 'divider',
                fontWeight: 600,
                fontSize: { xs: '0.875rem', sm: '1rem' },
                color: 'text.primary',
                whiteSpace: 'nowrap',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'action.hover',
                },
              }}
            >
              Sign In
            </Button>
            <Button
              variant="contained"
              component={RouterLink}
              to="/register"
              sx={{
                px: { xs: 2, sm: 3 },
                py: 1,
                minHeight: 40,
                fontWeight: 600,
                fontSize: { xs: '0.875rem', sm: '1rem' },
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                whiteSpace: 'nowrap',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              }}
            >
              Get Started
            </Button>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Header;